import React, { useEffect, useState } from "react";
import Footer from "components/Footer/Footer";
import "./mentorship.css";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { Loader2 } from "lucide-react";
import {
  getCurrentAppUser,
  isSupabaseConfigured,
  supabase,
} from "./lib/supabase";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Leaderboard from "./pages/Leaderboard";
import AdminPanel from "./pages/AdminPanel";

function LoadingScreen() {
  return (
    <div className="mentorship-page">
      <div className="mentorship-loading">
        <div className="mentorship-loading-box">
          <Loader2 style={{ width: "16px", height: "16px", animation: "spin 1s linear infinite", color: "#028D93" }} />
          Loading mentorship portal
        </div>
      </div>
      <Footer />
    </div>
  );
}

function ProtectedRoute() {
  const location = useLocation();
  const [state, setState] = useState({
    isLoading: true,
    isAllowed: false,
    error: null,
  });

  useEffect(() => {
    let isMounted = true;

    if (!isSupabaseConfigured) {
      setState({
        isLoading: false,
        isAllowed: false,
      });
      return undefined;
    }

    async function verifyAccess() {
      const { user, profile, error } = await getCurrentAppUser();

      if (!isMounted) return;

      setState({
        isLoading: false,
        isAllowed: Boolean(user && profile && !error),
        error: error?.message || null,
      });
    }

    verifyAccess();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      verifyAccess();
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  if (state.isLoading) {
    return <LoadingScreen />;
  }

  if (!state.isAllowed) {
    return (
      <Navigate
        replace
        to="/mentorship"
        state={{ from: location.pathname, error: state.error }}
      />
    );
  }

  return <Outlet />;
}

function AdminRoute() {
  const location = useLocation();
  const [state, setState] = useState({
    isLoading: true,
    isAllowed: false,
    error: null,
    targetPath: "/mentorship/dashboard",
  });

  useEffect(() => {
    let isMounted = true;

    if (!isSupabaseConfigured) {
      setState({
        isLoading: false,
        isAllowed: false,
        error: "Supabase is not configured yet.",
      });
      return undefined;
    }

    async function verifyAdminAccess() {
      const { user, profile, error } = await getCurrentAppUser();

      if (!isMounted) return;

      const isAdmin = Boolean(user && profile?.role === "admin" && !error);

      setState({
        isLoading: false,
        isAllowed: isAdmin,
        error: isAdmin
          ? null
          : error?.message || "Only admin accounts can access the mentorship admin panel.",
      });
    }

    verifyAdminAccess();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      verifyAdminAccess();
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  if (state.isLoading) {
    return <LoadingScreen />;
  }

  if (!state.isAllowed) {
    return (
      <Navigate
        replace
        to="/mentorship"
        state={{ from: location.pathname, error: state.error }}
      />
    );
  }

  return <Outlet />;
}

function MentorshipEntry() {
  const [state, setState] = useState({
    isLoading: true,
    isAllowed: false,
    error: null,
  });

  useEffect(() => {
    let isMounted = true;

    async function checkExistingSession() {
      if (!isSupabaseConfigured) {
        setState({ isLoading: false, isAllowed: false, error: null });
        return;
      }

      const { user, profile, error } = await getCurrentAppUser();

      if (!isMounted) return;

      setState({
        isLoading: false,
        isAllowed: Boolean(user && profile && !error),
        error: error?.message || null,
        targetPath: profile?.role === "admin" ? "/mentorship/admin" : "/mentorship/dashboard",
      });
    }

    checkExistingSession();

    return () => {
      isMounted = false;
    };
  }, []);

  if (state.isLoading) {
    return <LoadingScreen />;
  }

  if (state.isAllowed) {
    return <Navigate replace to={state.targetPath} />;
  }

  return <Login error={state.error} />;
}

export default function MentorshipApp() {
  return (
    <Routes>
      <Route path="/" element={<MentorshipEntry />} />
      <Route element={<ProtectedRoute />}>
        <Route path="dashboard" element={<Dashboard />} />
      </Route>
      <Route element={<AdminRoute />}>
        <Route path="admin" element={<AdminPanel />} />
      </Route>
      <Route path="leaderboard" element={<Leaderboard />} />
      <Route path="*" element={<Navigate replace to="/mentorship" />} />
    </Routes>
  );
}
